import { PROMPT_TEMPLATE_COLUMN_TYPE } from "./evaluate-drawer-utils";
import { useEvaluateScope } from "./useEvaluateScope";

import { EntityIcon } from "@/components/ui/icons";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { EntityType } from "@/queries";
import {
  PlaygroundEvaluateSheetReference,
  useUpdateEvaluateSheetReference,
} from "@/queries/playground/evaluate";
import { cn } from "@/lib/utils";

interface TemplateOverrideSelectProps {
  reference: PlaygroundEvaluateSheetReference;
  disabled: boolean;
}

const TemplateOverrideSelect = ({ reference, disabled }: TemplateOverrideSelectProps) => {
  const { queryScope } = useEvaluateScope();
  const updateReference = useUpdateEvaluateSheetReference(queryScope);
  const promptColumns = reference.columns.filter(
    column => column.type === PROMPT_TEMPLATE_COLUMN_TYPE,
  );
  const selectedColumn = promptColumns.find(
    column => column.id === reference.template_override?.column_id,
  );

  return (
    <div className="flex min-w-[260px] items-center gap-2 text-xs">
      <span className="shrink-0 font-medium text-gray-600">Prompt output</span>
      <Select
        value={reference.template_override?.column_id || undefined}
        disabled={disabled || updateReference.isPending || promptColumns.length === 0}
        onValueChange={columnId => {
          updateReference.mutate({
            referenceId: reference.id,
            body: { template_override: { column_id: columnId } },
          });
        }}
      >
        <SelectTrigger className="h-8 min-w-0 flex-1 gap-2 rounded-md border-gray-200 bg-white px-2.5 text-xs text-gray-700 shadow-none ring-0 transition-colors hover:border-gray-300 hover:bg-gray-50 focus:ring-1 focus:ring-gray-200 data-[state=open]:border-gray-300 data-[state=open]:bg-gray-50">
          <span className="flex min-w-0 items-center gap-2">
            <EntityIcon type={EntityType.PROMPT} size="xs" disabled={!selectedColumn} />
            <span className={cn("truncate", !selectedColumn && "text-gray-400")}>
              {selectedColumn?.title ?? "Choose output column…"}
            </span>
          </span>
        </SelectTrigger>
        <SelectContent className="rounded-lg border-gray-200 p-1 shadow-lg">
          {promptColumns.map(column => (
            <SelectItem
              key={column.id}
              value={column.id}
              className="rounded-md px-2 py-1.5 text-xs text-gray-700"
            >
              <span className="flex items-center gap-2">
                <EntityIcon type={EntityType.PROMPT} size="xs" />
                <span className="truncate">{column.title}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default TemplateOverrideSelect;
